import { Grid, TextInput } from "@mantine/core";
import {
  IconBriefcaseFilled,
  IconMail,
  IconUser,
  IconWorldWww,
} from "@tabler/icons-react";
import { formatValue } from "../../../utils/helper";
import type { BusinessInfoSettings } from "../../../types";

interface BusinessInfoFieldsProps {
  businessInfo: BusinessInfoSettings;
  setBusinessInfo: (businessInfo: BusinessInfoSettings) => void;
  minimize: boolean;
  calendlyLinkError: boolean;
  paymentLinkError: boolean;
  validateCalendlyLink: (link: string) => boolean;
  validatePaymentLink: (link: string) => boolean;
}

export const BusinessInfoFields = ({
  businessInfo,
  setBusinessInfo,
  minimize,
  calendlyLinkError,
  paymentLinkError,
  validateCalendlyLink,
  validatePaymentLink,
}: BusinessInfoFieldsProps) => {
  const handleCalendlyLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const enteredValue = e.target.value;
    if (validateCalendlyLink(enteredValue)) {
      setBusinessInfo({ ...businessInfo, consultLink: enteredValue });
    }
  };

  const handlePaymentLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const enteredValue = e.target.value;
    if (validatePaymentLink(enteredValue)) {
      setBusinessInfo({ ...businessInfo, paymentLink: enteredValue });
    }
  };

  const handlePhoneNumChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const formattedPhoneNum = formatValue("phone", value);
    setBusinessInfo({
      ...businessInfo,
      businessPhone: formattedPhoneNum,
    });
  };

  return (
    <Grid className="business-info-grid">
      <Grid.Col>
        <TextInput
          leftSection={<IconBriefcaseFilled color="#A3A9B0" />}
          placeholder={
            businessInfo.businessName
              ? businessInfo.businessName
              : "Business name"
          }
          className="business-name"
          label="Business name"
          name="firstName"
          radius="md"
          type="text"
          value={businessInfo.businessName}
          onChange={(e) =>
            setBusinessInfo({
              ...businessInfo,
              businessName: e.target.value,
            })
          }
          data-cy="businessNameInput"
        />
      </Grid.Col>
      <Grid.Col style={{ display: minimize ? "none" : "block" }}>
        <TextInput
          leftSection={<IconMail color="#A3A9B0" />}
          placeholder={
            businessInfo.businessEmail
              ? businessInfo.businessEmail
              : "Business email"
          }
          name="email"
          label="Business Email"
          radius="md"
          type="text"
          value={businessInfo.businessEmail}
          onChange={(e) =>
            setBusinessInfo({
              ...businessInfo,
              businessEmail: e.target.value,
            })
          }
          data-cy="businessEmailInput"
        />
      </Grid.Col>
      <Grid.Col style={{ display: minimize ? "none" : "block" }}>
        <TextInput
          leftSection={<IconUser color="#A3A9B0" />}
          placeholder={
            businessInfo.businessPhone
              ? businessInfo.businessPhone
              : "Phone number"
          }
          label="Phone number"
          name="phoneNumber"
          pattern="^\d{3}-\d{3}-\d{4}$"
          radius="md"
          type="text"
          value={businessInfo.businessPhone}
          onChange={handlePhoneNumChange}
          data-cy="businessPhoneNumberInput"
        />
      </Grid.Col>
      <Grid.Col>
        <TextInput
          leftSection={<IconWorldWww color="#A3A9B0" />}
          placeholder={
            businessInfo.businessWebsite
              ? businessInfo.businessWebsite
              : "Business website"
          }
          label="Company Website"
          name="website"
          radius="md"
          type="text"
          value={businessInfo.businessWebsite}
          onChange={(e) =>
            setBusinessInfo({
              ...businessInfo,
              businessWebsite: e.target.value,
            })
          }
          data-cy="businessWebsiteInput"
        />
      </Grid.Col>
      <Grid.Col>
        <TextInput
          leftSection={<IconWorldWww color="#A3A9B0" />}
          placeholder={
            businessInfo.consultLink
              ? businessInfo.consultLink
              : "Calendly / Scheduling App Link"
          }
          label="Consulting Link"
          name="consultLink"
          radius="md"
          type="text"
          value={businessInfo.consultLink}
          onChange={handleCalendlyLinkChange}
          error={calendlyLinkError && "Must start with 'https://'"}
          data-cy="calendlyLinkInput"
        />
      </Grid.Col>
      <Grid.Col>
        <TextInput
          leftSection={<IconWorldWww color="#A3A9B0" />}
          placeholder={
            businessInfo.paymentLink ? businessInfo.paymentLink : "Payment Link"
          }
          label="Payment Link"
          name="paymentLink"
          radius="md"
          type="text"
          value={businessInfo.paymentLink}
          onChange={handlePaymentLinkChange}
          error={paymentLinkError && "Must start with 'https://'"}
          data-cy="paymentLinkInput"
        />
      </Grid.Col>
    </Grid>
  );
};
